import { isProd } from './appEnv';

export interface SidebarNavItem {
  value: string;
  label: string;
  path: string;
}

export interface SidebarCategory {
  type: 'category';
  value: string;
  label: string;
  path?: string;
  items: SidebarNavItem[];
}

export interface SidebarItem {
  type: 'item';
  value: string;
  label: string;
  path: string;
}

export type SidebarConfig = (SidebarCategory | SidebarItem)[];

interface SidebarConfigMap {
  [key: string]: SidebarConfig;
}

/*
const crmNavItems: SidebarConfig = [
  { type: 'item', value: 'contacts', label: 'Customers', path: '/customers' },
  { type: 'item', value: 'customer-visits', label: 'Customer Visits', path: '/CustomerVisits' },
  { type: 'item', value: 'tickets', label: 'Tickets', path: '/Tickets' },
];
*/

const fleetNavItems: SidebarConfig = [
  { type: 'item', value: 'maintenance-active-reminders', label: 'Active Reminders', path: '/fleet/active-reminders' },
  { type: 'item', value: 'maintenance-history', label: 'Maintenance History', path: '/fleet/history' },
];

const devNavItems: SidebarConfig = [
  { type: 'item', value: 'api-testing', label: 'API Testing & Monitoring', path: '/dev/api-tools' },
  { type: 'item', value: 'db-utilities', label: 'Database Utilities', path: '/dev/db-tools' },
  { type: 'item', value: 'system-health', label: 'System Health', path: '/dev/system-health' },
  { type: 'item', value: 'dev-settings', label: 'Settings', path: '/dev/settings' },
  { type: 'item', value: 'order-dialog-preview', label: 'Order Dialog Preview', path: '/dev/order-dialog-preview' },
  { type: 'item', value: 'email-receipt', label: 'Receipt Email Exerciser', path: '/dev/email-receipt' },
  // PDF preview depends on the website react-pdf dev route, which is 403'd on
  // the prod host — hide it there (works on local + dev/sandbox deploys).
  ...(isProd() ? [] : [{ type: 'item' as const, value: 'pdf-templates', label: 'PDF Template Preview', path: '/dev/pdf-templates' }]),
  { type: 'item', value: 'pay-links', label: 'Pay-link Exerciser', path: '/dev/pay-links' },
];

export const settingsNavItems: SidebarConfig = [
  {
    type: 'category',
    value: 'general',
    label: 'General',
    path: '/settings/general',
    items: [
      { value: 'company-info', label: 'Company info', path: '/settings/general#company-info' },
      { value: 'address', label: 'Address', path: '/settings/general#address' },
      { value: 'localization', label: 'Localization', path: '/settings/general#localization' },
      { value: 'personalize-labels', label: 'Personalize your labels', path: '/settings/general#personalize-labels' },
    ]
  },
  {
    type: 'category',
    value: 'pricing',
    label: 'Pricing',
    path: '/settings/pricing',
    items: [
      { value: 'pricing-general', label: 'General', path: '/settings/pricing#general' },
      { value: 'pricing-structure-templates', label: 'Pricing structure templates', path: '/settings/pricing#pricing-structure-templates' },
      { value: 'advanced-pricing', label: 'Advanced pricing', path: '/settings/pricing#advanced-pricing' },
      { value: 'coupon-codes', label: 'Coupon codes', path: '/settings/pricing#coupon-codes' },
      { value: 'security-deposits', label: 'Security deposits', path: '/settings/pricing#security-deposits' },
    ]
  },
  { type: 'item', value: 'taxes', label: 'Taxes', path: '/settings/taxes' },
  { type: 'item', value: 'tier-map', label: 'Rate tier map', path: '/settings/tier-map' },
  { type: 'item', value: 'rental-period', label: 'Rental period', path: '/settings/rental-period' },
  { type: 'item', value: 'payments', label: 'Payment providers', path: '/settings/payments' },
  { type: 'item', value: 'authnet-review', label: 'Auth.Net linking', path: '/settings/authnet-review' },
  { type: 'item', value: 'custom-fields', label: 'Custom fields', path: '/settings/custom-fields' },
  { type: 'item', value: 'locations', label: 'Locations', path: '/settings/locations' },
  { type: 'item', value: 'sale-items', label: 'Sale items', path: '/settings/sale-items' },
  { type: 'item', value: 'emails', label: 'Emails', path: '/settings/emails' },
  { type: 'item', value: 'documents', label: 'Documents', path: '/settings/documents' },
  { type: 'item', value: 'translations', label: 'Translations', path: '/settings/locale' },
  { type: 'item', value: 'security', label: 'Security', path: '/settings/security' },
  { type: 'item', value: 'account', label: 'Account and billing', path: '/settings/account' },
];

export const sidebarConfig: SidebarConfigMap = {
  //'/crm': crmNavItems,
  //'/CustomerVisits': crmNavItems,
  //'/Tickets': crmNavItems,
  '/fleet': fleetNavItems,
  '/fleet/active-reminders': fleetNavItems,
  '/fleet/history': fleetNavItems,
  // Settings is intentionally NOT here: its nav now lives inside the page's
  // own workspace card (SettingsNavSidebar), part of the unified-surface
  // layout — not the global left Sidebar. See pages/SettingsLayout.tsx.
  '/dev': devNavItems,
};

// Resolve the breadcrumb leaf for a settings route — the label of the active
// section/page. Returns null for unmapped routes (breadcrumb shows "Settings"
// alone). Flat items match by exact path; categories match by path prefix so
// hash-anchored sub-pages still resolve to their section.
export const getActiveSettingsLabel = (pathname: string): string | null => {
  for (const item of settingsNavItems) {
    if (item.type === 'item' && item.path === pathname) return item.label;
    if (item.type === 'category' && item.path && pathname.startsWith(item.path)) return item.label;
  }
  return null;
};

// Helper function to determine which sidebar config to show based on current path
export const getSidebarConfigForRoute = (pathname: string): SidebarConfig | null => {
  // Check for exact match first
  if (sidebarConfig[pathname]) {
    return sidebarConfig[pathname];
  }

  // Check for parent route match (e.g., /crm/contacts should show /crm config)
  const parentRoute = '/' + pathname.split('/')[1];
  if (sidebarConfig[parentRoute]) {
    return sidebarConfig[parentRoute];
  }

  // Default to booking if at root
  if (pathname === '/' || pathname === '') {
    return sidebarConfig['/booking'];
  }

  return null;
};
